/**
 * Vosk Voice Trigger Hook
 *
 * Provides voice-activated triggers using Vosk Wasm-based speech recognition.
 * This replaces Web Speech API on platforms with mic exclusivity (Android).
 *
 * Uses Web Audio API to tap into an existing audio stream, allowing simultaneous
 * recording and speech recognition without mic contention.
 *
 * Architecture:
 *   AudioContext → SourceNode → AudioWorkletNode → PCM data → Vosk Recognizer
 *                      ↓
 *              (also connected to MediaRecorder via fan-out)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { loadModel, createRecognizer, isModelLoaded, isLoading } from '../services/voskService';

/**
 * Calculate Levenshtein edit distance between two strings
 * Used for fuzzy matching of misrecognized words
 */
const levenshteinDistance = (a, b) => {
  const matrix = Array(b.length + 1).fill(null)
    .map(() => Array(a.length + 1).fill(null));

  for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
  for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1,      // insertion
        matrix[j - 1][i] + 1,      // deletion
        matrix[j - 1][i - 1] + cost // substitution
      );
    }
  }
  return matrix[b.length][a.length];
};

/**
 * Check if word fuzzy-matches target keyword
 * Threshold: max 2 edits for words ≤6 chars, max 3 for longer words
 * Examples: "staat"→"start" (2 edits), "stab"→"stop" (2 edits)
 */
const fuzzyWordMatch = (word, keyword) => {
  if (word === keyword) return true;

  const maxDistance = keyword.length <= 6 ? 2 : 3;
  return levenshteinDistance(word, keyword) <= maxDistance;
};

/**
 * Check if a phrase matches in the transcript
 * Uses fuzzy matching to handle speech recognition errors
 *
 * @param {string} transcript - The recognized speech
 * @param {string} phrase - The phrase to match
 * @returns {boolean}
 */
const phraseMatches = (transcript, phrase) => {
  const normalizedTranscript = transcript.toLowerCase().trim();
  const normalizedPhrase = phrase.toLowerCase().trim();

  // Direct substring match (exact)
  if (normalizedTranscript.includes(normalizedPhrase)) {
    return true;
  }

  const transcriptWords = normalizedTranscript.split(/\s+/);
  const phraseWords = normalizedPhrase.split(/\s+/);

  // For each phrase word, check if any transcript word fuzzy-matches
  return phraseWords.every(phraseWord =>
    transcriptWords.some(transcriptWord =>
      fuzzyWordMatch(transcriptWord, phraseWord)
    )
  );
};

/**
 * Hook for Vosk-based voice trigger
 *
 * @param {Object} options
 * @param {boolean} options.enabled - Whether to listen for keywords
 * @param {AudioContext} options.audioContext - Web Audio API AudioContext
 * @param {AudioNode} options.sourceNode - Audio source node to tap into
 * @param {string[]} options.keywords - Keywords to detect
 * @param {function} options.onKeywordDetected - Callback when keyword found (keyword, timestamp)
 * @param {function} [options.onModelLoadProgress] - Callback during model download (percent)
 * @param {function} [options.onTranscript] - Optional callback for all transcripts (text, isFinal)
 * @param {function} [options.onError] - Optional callback for errors
 *
 * @returns {Object} { isListening, isModelReady, isModelLoading, lastHeardText, error }
 */
export const useVoskVoiceTrigger = ({
  enabled,
  audioContext,
  sourceNode,
  keywords = [],
  onKeywordDetected,
  onModelLoadProgress,
  onTranscript,
  onError
}) => {
  // State
  const [isListening, setIsListening] = useState(false);
  const [isModelReady, setIsModelReady] = useState(isModelLoaded());
  const [modelLoading, setModelLoading] = useState(isLoading());
  const [lastHeardText, setLastHeardText] = useState('');
  const [error, setError] = useState(null);

  // Refs for cleanup
  const workletNodeRef = useRef(null);
  const recognizerRef = useRef(null);
  const isCleaningUpRef = useRef(false);
  const hasTriggeredRef = useRef(false);

  // Sliding window for multi-word phrase detection
  // Accumulates recent words to detect phrases like "aufnahme start"
  const recentWordsRef = useRef([]);
  const wordTimeoutRef = useRef(null);
  const WORD_WINDOW_MS = 3000; // Keep words for 3 seconds

  // Store callbacks and keywords in refs to avoid dependency issues
  const onKeywordDetectedRef = useRef(onKeywordDetected);
  const onTranscriptRef = useRef(onTranscript);
  const onErrorRef = useRef(onError);
  const onModelLoadProgressRef = useRef(onModelLoadProgress);
  const keywordsRef = useRef(keywords);

  // Track audio message count for debugging
  const [audioMessageCount, setAudioMessageCount] = useState(0);
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    onKeywordDetectedRef.current = onKeywordDetected;
    onTranscriptRef.current = onTranscript;
    onErrorRef.current = onError;
    onModelLoadProgressRef.current = onModelLoadProgress;
    keywordsRef.current = keywords;
  }, [onKeywordDetected, onTranscript, onError, onModelLoadProgress, keywords]);

  // Load model when enabled
  useEffect(() => {
    console.log('[useVoskVoiceTrigger] Model load effect, enabled:', enabled, 'isModelLoaded:', isModelLoaded());
    if (!enabled) return;
    if (isModelLoaded()) {
      console.log('[useVoskVoiceTrigger] Model already loaded, setting ready');
      setIsModelReady(true);
      onModelLoadProgressRef.current?.(100); // Notify that model is ready
      return;
    }

    setModelLoading(true);
    console.log('[useVoskVoiceTrigger] Starting model load...');

    loadModel((progress) => {
      console.log('[useVoskVoiceTrigger] Model progress:', progress.percent);
      onModelLoadProgressRef.current?.(progress.percent);
    })
      .then(() => {
        setIsModelReady(true);
        setModelLoading(false);
        console.log('[useVoskVoiceTrigger] Model loaded successfully');
      })
      .catch((err) => {
        console.error('[useVoskVoiceTrigger] Model load failed:', err);
        setError(err);
        setModelLoading(false);
        onErrorRef.current?.(err);
      });
  }, [enabled]); // Removed onModelLoadProgress - using ref instead

  // Process transcript and check for keywords
  const processTranscript = useCallback((text, isFinal) => {
    if (!text) return;

    // Skip if in cooldown after last trigger
    const now = Date.now();
    if (hasTriggeredRef.current) {
      // Auto-reset after 2 second cooldown
      if (now - (hasTriggeredRef.triggerTime || 0) > 2000) {
        hasTriggeredRef.current = false;
        console.log('[useVoskVoiceTrigger] Cooldown ended, ready for next trigger');
      } else {
        return; // Still in cooldown
      }
    }

    setLastHeardText(text);
    onTranscriptRef.current?.(text, isFinal);

    // Check both partial and final results for faster response
    // Sliding window + fuzzy matching handles phrase detection correctly

    // Add new words to sliding window with timestamp
    const newWords = text.toLowerCase().trim().split(/\s+/).filter(w => w);
    newWords.forEach(word => {
      // Avoid duplicates from rapid results
      const lastWord = recentWordsRef.current[recentWordsRef.current.length - 1];
      if (!lastWord || lastWord.word !== word || now - lastWord.time > 500) {
        recentWordsRef.current.push({ word, time: now });
      }
    });

    // Prune old words outside the window
    recentWordsRef.current = recentWordsRef.current.filter(
      w => now - w.time < WORD_WINDOW_MS
    );

    // Build accumulated text from recent words
    const accumulatedText = recentWordsRef.current.map(w => w.word).join(' ');

    // Sort keywords by word count (multi-word first), then by length
    // This ensures "aufnahme start" is checked before "aufnahme"
    const sortedKeywords = [...keywordsRef.current].sort((a, b) => {
      const aWords = a.split(/\s+/).length;
      const bWords = b.split(/\s+/).length;
      if (bWords !== aWords) return bWords - aWords; // More words first
      return b.length - a.length; // Then longer strings
    });

    // Check for keyword matches in accumulated text
    for (const keyword of sortedKeywords) {
      if (phraseMatches(accumulatedText, keyword)) {
        console.log(`[useVoskVoiceTrigger] Keyword detected: "${keyword}" in "${accumulatedText}"`);
        hasTriggeredRef.current = true;
        hasTriggeredRef.triggerTime = now;
        recentWordsRef.current = []; // Clear buffer on trigger
        onKeywordDetectedRef.current?.(keyword, Date.now());
        break;
      }
    }
  }, []); // No dependencies - uses refs

  // Start/stop recognition based on enabled state and model readiness
  useEffect(() => {
    console.log('[useVoskVoiceTrigger] Recognition effect:', {
      enabled,
      isModelReady,
      hasAudioContext: !!audioContext,
      hasSourceNode: !!sourceNode,
      isCleaningUp: isCleaningUpRef.current
    });

    // Need all prerequisites
    if (!enabled || !isModelReady || !audioContext || !sourceNode) {
      console.log('[useVoskVoiceTrigger] Prerequisites not met, skipping recognition start');
      return;
    }

    // Don't start if already cleaning up
    if (isCleaningUpRef.current) {
      console.log('[useVoskVoiceTrigger] Cleanup in progress, skipping');
      return;
    }

    let isActive = true;
    hasTriggeredRef.current = false;

    const startRecognition = async () => {
      try {
        console.log('[useVoskVoiceTrigger] Starting recognition...');
        console.log('[useVoskVoiceTrigger] AudioContext state:', audioContext.state);
        console.log('[useVoskVoiceTrigger] Sample rate:', audioContext.sampleRate);

        // Resume AudioContext if suspended (required for user gesture)
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }

        // Load AudioWorklet module
        try {
          await audioContext.audioWorklet.addModule('/voskProcessor.js');
        } catch (e) {
          // Module might already be loaded
          if (!e.message.includes('already been added')) {
            throw e;
          }
        }

        // Create AudioWorklet node
        workletNodeRef.current = new AudioWorkletNode(audioContext, 'vosk-processor', {
          processorOptions: {
            bufferSize: 4096
          }
        });

        // Create Vosk recognizer at 16000Hz (model's native sample rate)
        // We'll downsample from AudioContext rate (typically 48000Hz) to 16000Hz
        const VOSK_SAMPLE_RATE = 16000;
        const RESET_AFTER_RESULTS = 100; // Reset recognizer after N results for stability
        let resultCount = 0;

        // Function to create/reset recognizer with event handlers
        const setupRecognizer = () => {
          // Cleanup previous recognizer
          if (recognizerRef.current) {
            try {
              recognizerRef.current.removeAllListeners?.();
              recognizerRef.current.remove();
            } catch (e) {
              // Ignore cleanup errors
            }
          }

          recognizerRef.current = createRecognizer(VOSK_SAMPLE_RATE);
          console.log('[useVoskVoiceTrigger] Recognizer created/reset');

          // vosk-browser uses event-based API for results
          if (typeof recognizerRef.current.on === 'function') {
            recognizerRef.current.on('result', (message) => {
              if (!isActive) return;
              // Don't check hasTriggeredRef here - let processTranscript handle cooldown
              resultCount++;
              console.log(`[useVoskVoiceTrigger] Result #${resultCount}:`, message.result?.text);
              if (message.result?.text) {
                processTranscript(message.result.text, true);
              }
              // Reset recognizer periodically for stability
              if (resultCount % RESET_AFTER_RESULTS === 0) {
                console.log(`[useVoskVoiceTrigger] Resetting recognizer after ${resultCount} results`);
                setupRecognizer();
              }
            });

            recognizerRef.current.on('partialresult', (message) => {
              if (!isActive) return;
              // Don't check hasTriggeredRef here - let processTranscript handle cooldown
              if (message.result?.partial) {
                processTranscript(message.result.partial, false);
              }
            });

            recognizerRef.current.on('error', (error) => {
              console.error('[useVoskVoiceTrigger] Recognizer error event:', error);
            });
          } else {
            console.warn('[useVoskVoiceTrigger] Recognizer does not have .on method - events not supported');
          }
        };

        // Initial recognizer setup
        setupRecognizer();

        const recMethods = Object.keys(recognizerRef.current);
        const hasOn = typeof recognizerRef.current.on === 'function';
        const hasAccept = typeof recognizerRef.current.acceptWaveform === 'function';
        console.log('[useVoskVoiceTrigger] Recognizer methods:', recMethods);

        // Surface debug info to UI
        setDebugInfo(`SR:${audioContext.sampleRate}→${VOSK_SAMPLE_RATE} on:${hasOn} accept:${hasAccept}`)

        // Track audio message count for debugging
        let localAudioCount = 0;
        const inputSampleRate = audioContext.sampleRate;
        const downsampleRatio = inputSampleRate / VOSK_SAMPLE_RATE; // e.g., 48000/16000 = 3

        // Simple downsampling function
        const downsample = (buffer, ratio) => {
          if (ratio === 1) return buffer;
          const newLength = Math.floor(buffer.length / ratio);
          const result = new Float32Array(newLength);
          for (let i = 0; i < newLength; i++) {
            result[i] = buffer[Math.floor(i * ratio)];
          }
          return result;
        };

        // Handle audio data from worklet
        workletNodeRef.current.port.onmessage = (event) => {
          if (!isActive || hasTriggeredRef.current) return;

          if (event.data.type === 'audio' && recognizerRef.current) {
            localAudioCount++;
            // Update state every 10 messages to avoid too many re-renders
            if (localAudioCount % 10 === 0) {
              setAudioMessageCount(localAudioCount);
            }
            if (localAudioCount === 1) {
              console.log('[useVoskVoiceTrigger] First audio message received from worklet');
              console.log('[useVoskVoiceTrigger] Input sample rate:', inputSampleRate);
              console.log('[useVoskVoiceTrigger] Downsample ratio:', downsampleRatio);
            } else if (localAudioCount % 100 === 0) {
              console.log(`[useVoskVoiceTrigger] Audio messages received: ${localAudioCount}`);
            }

            try {
              // Downsample audio from input rate (48000Hz) to Vosk rate (16000Hz)
              const float32Data = event.data.buffer;
              const downsampled = downsample(float32Data, downsampleRatio);

              // Log first chunk details for debugging
              if (localAudioCount === 1) {
                console.log('[useVoskVoiceTrigger] Audio chunk details:', {
                  originalLength: float32Data.length,
                  downsampledLength: downsampled.length,
                  sampleValues: [downsampled[0], downsampled[100], downsampled[500]].map(v => v?.toFixed(4))
                });
              }

              // Feed downsampled audio to Vosk (Float32Array + sample rate)
              recognizerRef.current.acceptWaveformFloat(downsampled, VOSK_SAMPLE_RATE);
            } catch (err) {
              console.error('[useVoskVoiceTrigger] Recognizer error:', err);
              if (localAudioCount <= 5) {
                console.error('[useVoskVoiceTrigger] Error details:', err.message, err.stack);
              }
            }
          }
        };

        // Connect source → worklet (tap into audio stream)
        sourceNode.connect(workletNodeRef.current);

        setIsListening(true);
        setError(null);
        console.log('[useVoskVoiceTrigger] Recognition started');

      } catch (err) {
        console.error('[useVoskVoiceTrigger] Failed to start recognition:', err);
        setError(err);
        setIsListening(false);
        onErrorRef.current?.(err);
      }
    };

    startRecognition();

    // Cleanup function
    return () => {
      console.log('[useVoskVoiceTrigger] Cleaning up...');
      isActive = false;
      isCleaningUpRef.current = true;

      // Stop worklet
      if (workletNodeRef.current) {
        try {
          workletNodeRef.current.port.postMessage({ type: 'stop' });
          workletNodeRef.current.disconnect();
        } catch (e) {
          // Ignore disconnect errors
        }
        workletNodeRef.current = null;
      }

      // Cleanup recognizer
      if (recognizerRef.current) {
        try {
          // Remove event listeners
          recognizerRef.current.removeAllListeners?.();
          recognizerRef.current.remove();
        } catch (e) {
          // Ignore cleanup errors
        }
        recognizerRef.current = null;
      }

      setIsListening(false);
      isCleaningUpRef.current = false;
    };
  }, [enabled, isModelReady, audioContext, sourceNode, processTranscript]);

  return {
    isListening,
    isModelReady,
    isModelLoading: modelLoading,
    lastHeardText,
    error,
    audioMessageCount,
    debugInfo
  };
};

/**
 * Check if the current platform should use Vosk (Android)
 * @returns {boolean}
 */
export const shouldUseVosk = () => {
  return /android/i.test(navigator.userAgent);
};
